import { Fragment, PureComponent } from 'react';
import { GlobalState } from '../../../store/reducers';
import { getGenieSetting } from '../../../store/selectors/session';
import { connect } from 'react-redux';
import { getAclViewClassName } from '../../../restrictions/utils';
import { SREV2 } from '../../../api/pushGenie';
import getPatientHealthSummaryWithPooling, {
    PatientHealthSummaryWithPoolingResponse,
} from '../../../api/getPatientHealthSummaryWithPooling';
import PatientSummaryMagicBox from './PatientSummaryMagicBox';
import { getPatient, getPatientHealthSummary } from '../../../store/selectors/prescriptionPad';
import { Actions } from '../../../store/actions';
import { ThunkDispatch } from 'redux-thunk';
import { setPatientHealthSummary } from '../../../store/actions/prescriptionPad';
import { track } from '../../../tracking';
import { DocAssistIcon } from '../../../components/common/Icons';
import { Patient } from '../../../store/types/prescription';

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    patientHealthSummary?: PatientHealthSummaryWithPoolingResponse;
    hidePatientHealthSummary?: boolean;
    patient?: Patient;
};

type State = {
    isFetching: boolean;
};

class PatientSummaryComponent extends PureComponent<Props, State> {
    state: State = {
        isFetching: false,
    };

    attempt = 0;
    isMagicContainerOpen = localStorage.getItem('isMagicContainerOpen') || '';
    maxAttempts = 4;
    attemptInterval = [6000, 20000, 25000];
    pooling: NodeJS.Timeout | undefined = undefined;

    componentWillUnmount = (): void => {
        if (this.pooling) {
            clearTimeout(this.pooling);
        }
    };

    fetchHealthSummary = async (patientId: string, information_hash?: string) => {
        const { dispatch } = this.props;
        try {
            const data = await getPatientHealthSummaryWithPooling(patientId, information_hash);
            if (data.status === 'SUCCESS') {
                if (data?.most_recent_summary) {
                    dispatch(setPatientHealthSummary(data));
                } else {
                    dispatch(setPatientHealthSummary({ status: 'ERROR' }));
                }
                track('FETCH_PATIENT_HEALTH_SUMMARY_WITH_POOLING_SUCCESS', {
                    patient_id: patientId,
                    from: 'fetchPatientHealthSummary',
                    attempt: `${this.attempt}`,
                    status: 'SUCCESS',
                    isMagicBoxOpen: this.isMagicContainerOpen,
                });
            } else if (data.status === 'NO_DATA') {
                dispatch(setPatientHealthSummary(data));
                track('FETCH_PATIENT_HEALTH_SUMMARY_WITH_POOLING_NO_DATA', {
                    patient_id: patientId,
                    from: 'fetchPatientHealthSummary',
                    attempt: `${this.attempt}`,
                    status: 'NO_DATA',
                    isMagicBoxOpen: this.isMagicContainerOpen,
                });
            } else {
                if (this.attempt < this.maxAttempts) {
                    track('FETCH_PATIENT_HEALTH_SUMMARY_WITH_POOLING_PENDING', {
                        patient_id: patientId,
                        from: 'fetchPatientHealthSummary',
                        attempt: `${this.attempt}`,
                        status: data.status,
                        isMagicBoxOpen: this.isMagicContainerOpen,
                    });
                    dispatch(setPatientHealthSummary(data));
                    this.pooling = setTimeout(() => {
                        this.fetchHealthSummary(patientId, data.information_hash);
                    }, this.attemptInterval[this.attempt]);
                    this.attempt++;
                } else {
                    dispatch(setPatientHealthSummary({ status: 'ERROR' }));
                    track('FETCH_PATIENT_HEALTH_SUMMARY_WITH_POOLING_ERROR', {
                        patient_id: patientId,
                        from: 'fetchPatientHealthSummary',
                        attempt: `${this.attempt}`,
                        status: 'ERROR',
                        isMagicBoxOpen: this.isMagicContainerOpen,
                    });
                }
            }
        } catch (error) {
            dispatch(setPatientHealthSummary({ status: 'ERROR' }));
            track('FETCH_PATIENT_HEALTH_SUMMARY_WITH_POOLING_ERROR', {
                patient_id: patientId,
                from: 'fetchPatientHealthSummary',
                attempt: `${this.attempt}`,
                status: 'ERROR',
                isMagicBoxOpen: this.isMagicContainerOpen,
            });
        }
    };

    onClick = () => {
        const { patient } = this.props;
        const { fetchHealthSummary } = this;
        const patientId = patient?.id || '';
        if (patientId) {
            fetchHealthSummary(patientId);
        }
        this.setState({
            isFetching: true,
        });
        track('CLICK_GENERATE_PATIENT_SUMMARY', { patientId });
    };

    render() {
        const { patientHealthSummary, hidePatientHealthSummary } = this.props;
        const { isFetching } = this.state;
        const { onClick } = this;

        if (hidePatientHealthSummary) {
            return null;
        }

        return isFetching || patientHealthSummary ? (
            <div className={`px-8 pt-12 pb-8 space-y-4 ${getAclViewClassName(SREV2.DOC_ASSIST)}`}>
                {patientHealthSummary?.status === 'NO_DATA' ? (
                    <div className="flex items-center text-darwin-neutrals-400 text-12 space-x-8">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                            >
                                <g
                                    style={{
                                        mixBlendMode: 'multiply',
                                    }}
                                >
                                    <circle cx="8.61533" cy="8.34555" r="5.38462" fill="#215FFF" />
                                </g>
                                <g filter="url(#filter0_b_66_4098)">
                                    <circle
                                        cx="3.76923"
                                        cy="4.03876"
                                        r="3.76923"
                                        fill="#B45EDC"
                                        fill-opacity="0.6"
                                    />
                                </g>
                                <defs>
                                    <filter
                                        id="filter0_b_66_4098"
                                        x="-1.07692"
                                        y="-0.807392"
                                        width="9.69242"
                                        height="9.69291"
                                        filterUnits="userSpaceOnUse"
                                        color-interpolation-filters="sRGB"
                                    >
                                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                        <feGaussianBlur
                                            in="BackgroundImageFix"
                                            stdDeviation="0.538462"
                                        />
                                        <feComposite
                                            in2="SourceAlpha"
                                            operator="in"
                                            result="effect1_backgroundBlur_66_4098"
                                        />
                                        <feBlend
                                            mode="normal"
                                            in="SourceGraphic"
                                            in2="effect1_backgroundBlur_66_4098"
                                            result="shape"
                                        />
                                    </filter>
                                </defs>
                            </svg>
                        </div>
                        <span>
                            EkaDocAssist AI needs at least 2 visits to generate patient summary
                        </span>
                    </div>
                ) : patientHealthSummary?.status === 'ERROR' ? (
                    <div className="flex items-center text-darwin-neutrals-600 text-12 space-x-8">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="21"
                                height="22"
                                viewBox="0 0 21 22"
                                fill="none"
                            >
                                <g
                                    style={{
                                        mixBlendMode: 'multiply',
                                    }}
                                >
                                    <circle cx="12.9231" cy="13.0184" r="8.07696" fill="#979191" />
                                </g>
                                <circle cx="10.125" cy="12.125" r="1.125" fill="white" />
                                <circle cx="16.125" cy="12.125" r="1.125" fill="white" />
                                <path
                                    d="M10.696 17V17C12.0585 16.0917 13.8336 16.0917 15.1961 17V17"
                                    stroke="white"
                                    stroke-width="1.50001"
                                />
                                <g filter="url(#filter0_b_95_4773)">
                                    <circle
                                        cx="5.65387"
                                        cy="6.55817"
                                        r="5.65387"
                                        fill="#737373"
                                        fill-opacity="0.5"
                                    />
                                </g>
                                <defs>
                                    <filter
                                        id="filter0_b_95_4773"
                                        x="-1.61539"
                                        y="-0.711094"
                                        width="14.5384"
                                        height="14.5394"
                                        filterUnits="userSpaceOnUse"
                                        color-interpolation-filters="sRGB"
                                    >
                                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                        <feGaussianBlur
                                            in="BackgroundImageFix"
                                            stdDeviation="0.807696"
                                        />
                                        <feComposite
                                            in2="SourceAlpha"
                                            operator="in"
                                            result="effect1_backgroundBlur_95_4773"
                                        />
                                        <feBlend
                                            mode="normal"
                                            in="SourceGraphic"
                                            in2="effect1_backgroundBlur_95_4773"
                                            result="shape"
                                        />
                                    </filter>
                                </defs>
                            </svg>
                        </div>
                        <span className="text-12">
                            Oops! Looks like Eka DocAssist needs servicing. Please try again soon!
                        </span>
                    </div>
                ) : patientHealthSummary?.status === 'PENDING' ? (
                    <div className="flex items-center text-darwin-neutrals-400 text-12 space-x-8">
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                            >
                                <g
                                    style={{
                                        mixBlendMode: 'multiply',
                                    }}
                                >
                                    <circle cx="8.61533" cy="8.34555" r="5.38462" fill="#215FFF" />
                                </g>
                                <g filter="url(#filter0_b_66_4098)">
                                    <circle
                                        cx="3.76923"
                                        cy="4.03876"
                                        r="3.76923"
                                        fill="#B45EDC"
                                        fill-opacity="0.6"
                                    />
                                </g>
                                <defs>
                                    <filter
                                        id="filter0_b_66_4098"
                                        x="-1.07692"
                                        y="-0.807392"
                                        width="9.69242"
                                        height="9.69291"
                                        filterUnits="userSpaceOnUse"
                                        color-interpolation-filters="sRGB"
                                    >
                                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                        <feGaussianBlur
                                            in="BackgroundImageFix"
                                            stdDeviation="0.538462"
                                        />
                                        <feComposite
                                            in2="SourceAlpha"
                                            operator="in"
                                            result="effect1_backgroundBlur_66_4098"
                                        />
                                        <feBlend
                                            mode="normal"
                                            in="SourceGraphic"
                                            in2="effect1_backgroundBlur_66_4098"
                                            result="shape"
                                        />
                                    </filter>
                                </defs>
                            </svg>
                        </div>
                        <span className="text-12">Analysing patients past visits...</span>
                    </div>
                ) : patientHealthSummary?.status === 'SUCCESS' ||
                  patientHealthSummary?.status === 'IN_PROGRESS' ? (
                    <PatientSummaryMagicBox
                        patientHealthSummary={patientHealthSummary}
                        from="patient-summary-magic-box"
                    />
                ) : (
                    <div className="flex items-center text-darwin-neutrals-400 text-12 space-x-8">
                        {DocAssistIcon('w-14 h-14')}
                        <span>AI Patient Summary will be ready in 30 seconds</span>
                    </div>
                )}
                <p className="w-full flex justify-center">
                    <p className="h-4 rounded-full bg-darwin-neutral-50 w-full mx-8"></p>
                </p>
            </div>
        ) : (
            <div className={`space-y-4 pt-12 px-8 ${getAclViewClassName(SREV2.DOC_ASSIST)}`}>
                <button
                    className="w-full flex items-center justify-center space-x-4 px-7 py-5 rounded-4 border border-darwin-accent-bright-blue-800"
                    onClick={onClick}
                >
                    <p> {DocAssistIcon('w-14 h-14')}</p>
                    <p className="text-12 font-700 text-darwin-accent-bright-blue-800">
                        Generate patient summary
                    </p>
                </button>
                <p className="text-11 text-darwin-neutrals-400">Powered by Eka DocAssist AI</p>
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    patientHealthSummary: getPatientHealthSummary(state),
    hidePatientHealthSummary: getGenieSetting(state, 'hidePatientHealthSummary') as boolean,
    patient: getPatient(state),
}))(PatientSummaryComponent);
